import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS, BRAND, CONTACT } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { Logo } from "@/components/site/Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (linkOrId) => {
    setOpen(false);
    const link = typeof linkOrId === "string" ? { id: linkOrId } : linkOrId;
    if (link.route) return navigate(link.route);
    if (!link.id) return;
    if (location.pathname !== "/") return navigate("/#" + link.id);
    scrollToId(link.id);
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#0B132B]/80 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="group"
        >
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l)}
              className="font-label text-xs text-[#F8F5F2]/80 hover:text-primary transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="nav-directions-btn"
            href={CONTACT.mapsUrl}
            target="_blank"
            rel="noreferrer"
          >
            <Button className="rounded-full font-label text-[11px] px-5 h-10 hover:-translate-y-0.5 active:scale-95 transition-transform">
              <MapPin className="h-4 w-4" /> Get Directions
            </Button>
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#F8F5F2] p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="md:hidden overflow-hidden bg-[#0B132B]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l)}
                  className="font-label text-sm text-left text-[#F8F5F2]/85 hover:text-primary transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a href={CONTACT.mapsUrl} target="_blank" rel="noreferrer">
                <Button className="w-full rounded-full font-label text-xs mt-2">
                  <MapPin className="h-4 w-4" /> Get Directions
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
